import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import { api, isApiError, type ApiClient, type ApiUser } from '@/shared/api';
import { ROUTES } from '@/shared/config/routes';

/**
 * Who is signed in.
 *
 * Sanctum keeps the session in a cookie, so the only way to ask is to ask the
 * server. A 401 is not a failure here — it is the answer "nobody", and the
 * guards below act on it rather than showing an error.
 */

export const masterSessionKey = ['session', 'master'] as const;
export const clientSessionKey = ['session', 'client'] as const;

/** Treats "not signed in" as a value, and anything else as a real failure. */
const orNull = async <T>(request: Promise<T>): Promise<T | null> => {
  try {
    return await request;
  } catch (error) {
    // 404 covers an account that exists but has no profile of this kind — a
    // master asking the client endpoint, say.
    if (isApiError(error) && (error.status === 401 || error.status === 404)) return null;
    throw error;
  }
};

export const useMasterSession = () => {
  const query = useQuery({
    queryKey: masterSessionKey,
    queryFn: () => orNull(api.get<ApiUser>('/auth/me')),
    retry: false,
    staleTime: 5 * 60_000,
  });

  return {
    user: query.data ?? null,
    isAuthenticated: Boolean(query.data),
    isLoading: query.isPending,
  };
};

export const useClientSession = () => {
  const query = useQuery({
    queryKey: clientSessionKey,
    queryFn: () => orNull(api.get<ApiClient>('/client/auth/me')),
    retry: false,
    staleTime: 5 * 60_000,
  });

  return {
    client: query.data ?? null,
    isAuthenticated: Boolean(query.data),
    isLoading: query.isPending,
  };
};

/** Ends the master's session and empties the cache. */
export const useLogout = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: () => api.post<null>('/auth/logout'),
    // Clear on settled, not on success: if the request failed because the
    // session was already gone, the cached bookings and clients of whoever was
    // signed in must not survive into the next person's session.
    onSettled: () => {
      queryClient.clear();
      void navigate(ROUTES.auth, { replace: true });
    },
  });
};

/**
 * Ends every session of this account (`POST /auth/sessions/revoke`) — other
 * devices on their next request, this one now — then leaves for sign-in.
 */
export const useSignOutEverywhere = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: () => api.post<null>('/auth/sessions/revoke'),
    onSettled: () => {
      queryClient.clear();
      void navigate(ROUTES.auth, { replace: true });
    },
  });
};
